import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Persisted corner the launch button (and the floating panel that grows out of it) anchor to.
// Six zones: a 2-row × 3-col viewport grid.
export type ChatWidgetPosition = 'bottom-right' | 'bottom-center' | 'bottom-left' | 'top-right' | 'top-center' | 'top-left';
const DEFAULT_POSITION: ChatWidgetPosition = 'bottom-right';
const POSITIONS: readonly ChatWidgetPosition[] = ['bottom-right', 'bottom-center', 'bottom-left', 'top-right', 'top-center', 'top-left'];
const isPosition = (value: unknown): value is ChatWidgetPosition => typeof value === 'string' && (POSITIONS as readonly string[]).includes(value);

// Anchor classes per position, spacing-agnostic via --percy-gap so the button (1.5rem)
// and floating card (1rem) share one map. Center uses translate so it centers at any
// width; the morph reads the post-transform rect, so its FLIP math holds.
// The horizontal edge adds --percy-inset-x (a right-drawer's occupied width, 0 by default) so an open
// drawer slides the launcher along its edge — vertical stays on --percy-gap so it never lifts off.
// Full class strings are spelled out (no interpolation) so Tailwind's JIT scanner emits each utility.
export const POSITION_ANCHOR: Record<ChatWidgetPosition, string> = {
  'bottom-right': 'bottom-[var(--percy-gap)] right-[calc(var(--percy-gap)_+_var(--percy-inset-x,0px))]',
  'bottom-center': 'bottom-[var(--percy-gap)] left-1/2 -translate-x-1/2',
  'bottom-left': 'bottom-[var(--percy-gap)] left-[calc(var(--percy-gap)_+_var(--percy-inset-x,0px))]',
  'top-right': 'top-[var(--percy-gap)] right-[calc(var(--percy-gap)_+_var(--percy-inset-x,0px))]',
  'top-center': 'top-[var(--percy-gap)] left-1/2 -translate-x-1/2',
  'top-left': 'top-[var(--percy-gap)] left-[calc(var(--percy-gap)_+_var(--percy-inset-x,0px))]'
};

// Which zone a release at (x, y) lands in — viewport split into a 2-row × 3-col grid.
export const snapToPosition = (x: number, y: number): ChatWidgetPosition => {
  const col = x < window.innerWidth / 3 ? 'left' : x < window.innerWidth * 2 / 3 ? 'center' : 'right';
  const row = y < window.innerHeight / 2 ? 'top' : 'bottom';
  return `${row}-${col}` as ChatWidgetPosition;
};
type ChatWidgetPositionState = {
  position: ChatWidgetPosition;
  setPosition: (next: ChatWidgetPosition) => void;
};
const useStore = create<ChatWidgetPositionState>()(persist(set => ({
  position: DEFAULT_POSITION,
  setPosition: next => set({
    position: next
  })
}), {
  name: 'percy.chat-widget-position',
  // Coerce an unknown persisted value back to the default rather than trusting it.
  merge: (persisted, current) => {
    const position = isPosition((persisted as Partial<ChatWidgetPositionState>)?.position) ? (persisted as ChatWidgetPositionState).position : DEFAULT_POSITION;
    return {
      ...current,
      position
    };
  }
}));
const useChatWidgetPosition = () => {
  return useStore();
};
export default useChatWidgetPosition;